// ============================================================================
// PROVENANCE — the label, and the gate in front of it.
//
// docs/design/handoff/README.md §Overview: "Anything the AI generated (summaries, flashcards,
// syllabus extracts) carries a provenance label. … Implement these as invariants, not as copy."
//
// So this module does two things, and the second is the important one:
//   1. It renders the label, in the mockup's exact eyebrow geometry.
//   2. `<Provenanced>` REFUSES TO RENDER its children when the artifact cannot say where it came
//      from. Not a warning next to the text, not a greyed-out variant — nothing. An unlabelled
//      machine-written paragraph on a lecture page is indistinguishable from something the
//      professor actually said, and that is the one failure this product cannot ship.
// ============================================================================

import type { ReactNode } from 'react'
import { hasProvenance, monthName, msToTimestamp, toDate, type AiArtifact } from '@app/shared'

/**
 * 'Sep 10' — the date an artifact was generated, read off the string rather than through UTC.
 * A provenance label with the wrong day is worse than none: it invites the reader to trust a
 * summary that predates the lecture it claims to summarise.
 */
function generatedOn(generatedAt: string): string {
  const date = generatedAt.slice(0, 10)
  return `${monthName(date, true)} ${toDate(date).getUTCDate()}`
}

/**
 * The label itself: '<what> · Sep 10', or '<what> · 12:31' when the artifact points at one
 * moment in a recording.
 *
 *   'generated from transcript · Sep 10'      a summary
 *   'generated from this lecture · 12:31'     a flashcard drawn from one segment
 *   'extracted from syllabus PDF · Aug 26'    the syllabus panel
 *
 * `what` is the caller's, because the verb differs by artifact and the handoff's wording is
 * data, not decoration.
 */
export function ProvenanceLabel({
  what,
  artifact,
  sourceMs,
  className,
}: {
  what: string
  artifact: AiArtifact
  /** When set, the label cites the moment instead of the day. */
  sourceMs?: number | null
  className?: string
}) {
  const stamp =
    sourceMs !== null && sourceMs !== undefined
      ? msToTimestamp(sourceMs)
      : generatedOn(artifact.provenance.generatedAt)

  return (
    <span
      className={className ?? 'text-[9.5px] font-normal leading-none text-faintest-2'}
      // The model is not on screen at rest — it would crowd a 9.5px eyebrow on every card — but
      // it is one hover away, because "which model wrote this" is a fair question to be able to
      // answer without opening a database.
      title={`${artifact.provenance.model} · from ${artifact.provenance.sourceRef} · generated ${artifact.provenance.generatedAt}`}
    >
      {what} · {stamp}
    </span>
  )
}

/**
 * The gate. Children render only if the artifact carries { model, sourceRef, generatedAt }.
 *
 * `fallback` exists for the case where the ABSENCE is itself worth saying — "no summary has been
 * generated for this lecture" is useful, an empty panel is not. It defaults to nothing, which is
 * the safe answer.
 */
export function Provenanced<T extends AiArtifact>({
  artifact,
  children,
  fallback = null,
}: {
  artifact: T | null | undefined
  children: (artifact: T) => ReactNode
  fallback?: ReactNode
}) {
  if (!hasProvenance(artifact)) return <>{fallback}</>
  return <>{children(artifact)}</>
}

/** The eyebrow that sits beside a provenance label: 'AI SUMMARY', 'KEY CONCEPTS'. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={[
        'text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </span>
  )
}
